import { useMemo } from 'react';
import Image from '@/components/atoms/image copy';
import Slider from '@/components/atoms/slider';
import useMediaQuery from '@/helpers/hooks/useMediaQuery';
import useWindowWidth from '@/helpers/hooks/useBrowerWidth';
import useTouchDevice from '@/hooks/useTouchDevice';
import {
  desktop,
  largeDesktop,
} from '@/helpers/utils/screensizes';
import { useParams, usePathname } from 'next/navigation';

export interface ImageSliderProps {
  data: any;
}

const NewsImageSlider: React.FC<ImageSliderProps> = ({ data }) => {
  const { isTouchDevice } = useTouchDevice();

  const [isLargeDesktopSize] = useMediaQuery(largeDesktop);
  const [isDesktopSize] = useMediaQuery(desktop);

  const width = useWindowWidth();
  const isMobileView = width !== null && width < 1024;

  const pathname = usePathname();
  const { locale } = useParams();

  const isHomePage = pathname === `/${locale}/`;

  /* ---------- DATA ---------- */
  const cards =
    data?.sections?.[0]?.section_with_image_and_html?.cards || [];

  /* ---------- SLIDER ARROWS ---------- */
  const showArrows = useMemo(
    () =>
      !isTouchDevice &&
      !(
        (isLargeDesktopSize && cards.length <= 4) ||
        (!isLargeDesktopSize && cards.length <= 2)
      ),
    [isLargeDesktopSize, isTouchDevice, cards.length],
  );

  return (
    <div className="bg-[#F3EDE9]">
      <div className="lg:px-[93px] px-[43px] py-6 w-full">
        <h1 className="text-[20px] lg:text-36 font-semibold text-[#090909] pb-4">
          News
        </h1>

        <div className="relative">
          <Slider
            containerClassName={showArrows ? 'padding-x-48-sm' : ''}
            slidesPerView={isDesktopSize ? 1 : 1.15}
            spaceBetween={8}
            autoplay={{
              delay: 5000,
              disableOnInteraction: false,
            }}
            dots={false}
            arrows={isDesktopSize}
            allowArrowsOnTouchDevice
            innerArrows
            allowTouchMove={true}
            prevButtonStyles={{
              top: 'auto',
              bottom: '0',
              right: '56px',
              left: 'auto',
              padding: '10px',
            }}
            nextButtonStyles={{
              top: 'auto',
              bottom: '0',
              right: '0',
              left: 'auto',
              padding: '10px',
            }}
          >
            {cards.map((card: any, index: number) => {
              const imageSrc = card?.desktop_image?.url;
              const imageAlt = card?.desktop_image?.title || '';
              const rawUrl = (() => {
                const direct = card?.link || card?.url || card?.ctas?.[0]?.button?.href || '';
                if (direct) return direct;
                const match = (card?.html_text || '').match(/href="([^"]+)"/);
                return match ? match[1] : '';
              })();
              const articleUrl = rawUrl
                ? rawUrl.startsWith('http')
                  ? rawUrl
                  : `/${locale}${rawUrl.startsWith('/') ? rawUrl : `/${rawUrl}`}`
                : '';

              return (
                <div key={index}>
                  {/* MATCHED LAYOUT FROM ImageContentSlider */}
                  <div
                    className={
                      isMobileView
                        ? 'flex flex-col h-[460px] overflow-hidden rounded-lg'
                        : 'grid grid-cols-[60%_40%] overflow-hidden rounded-lg'
                    }
                  >
                    {/* IMAGE */}
                    <a
                      href={articleUrl || undefined}
                      className={`w-full overflow-hidden block ${articleUrl ? 'cursor-pointer' : 'cursor-default'}`}
                    >
                      <div
                        className={`relative w-full ${
                          isMobileView ? 'h-[220px]' : 'h-full aspect-[5/2]'
                        }`}
                      >
                        {imageSrc && (
                          <Image
                            src={imageSrc}
                            alt={imageAlt}
                            fill={!isMobileView}
                            width={isMobileView ? 1200 : undefined}
                            height={isMobileView ? 800 : undefined}
                            className={
                              isMobileView
                                ? 'w-full h-full object-cover'
                                : 'object-cover'
                            }
                            sizes="(max-width: 768px) 100vw, 60vw"
                          />
                        )}
                      </div>
                    </a>

                    {/* CONTENT */}
                    <div className="bg-white flex flex-1 overflow-hidden">
                      <a
                        href={articleUrl || undefined}
                        className={`p-4 md:p-6 lg:p-10 flex flex-col justify-center items-start text-left w-full text-sm md:text-base lg:text-lg overflow-hidden no-underline ${articleUrl ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                      >
                        <div
                          className="line-clamp-6 w-full"
                          dangerouslySetInnerHTML={{
                            __html: card?.html_text || '',
                          }}
                        />
                      </a>
                    </div>
                  </div>

                  {/* CMS CSS */}
                  {card?.additional_css_classes && (
                    <style>{card.additional_css_classes}</style>
                  )}
                </div>
              );
            })}
          </Slider>
        </div>
      </div>
    </div>
  );
};

export default NewsImageSlider;